import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, qs, useAction, useApi } from '../api';
import { useCan } from '../auth';
import { Account, Ago, AlertStatus, Approval, browserLabel, catLabel, clickableRow, Empty, EntityLink, fmtDate, ListToolbar, MutationError, PageHead, PAGE_SIZE, Pager, Protection, Q, Section, Severity, SortTh, useFilters } from '../ui/kit';
import { EventTable, useEventDrawer } from './Activity';

export function Users() {
  const filters = useFilters();
  const { sp, set, offset, setOffset } = filters;
  const nav = useNavigate();
  const q = useApi<{ total: number; items: any[] }>(`/users${qs({ q: sp.get('q'), groupId: sp.get('groupId'), sort: sp.get('sort'), limit: PAGE_SIZE, offset })}`);
  const groups = useApi<Array<{ id: string; name: string }>>('/groups');
  return (
    <>
      <PageHead title="Users" sub="People in your directory (synchronised from Entra ID) and corporate identities reported by enrolled browsers." />
      <ListToolbar filters={filters} placeholder="Search name, email or department…">
        <select aria-label="Group" value={sp.get('groupId') ?? ''} onChange={(e) => set('groupId', e.target.value)}>
          <option value="">All groups</option>
          {groups.data?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </ListToolbar>
      <Q q={q}>
        {(d) =>
          d.items.length === 0 ? <Empty title="No users">{filters.active() ? 'No user matches these filters.' : 'Connect Microsoft Entra ID under Integrations to synchronise your directory.'}</Empty> : (
            <>
              <div className="table-wrap"><table className="t">
                <thead><tr><th>User</th><th>Department</th><th>Source</th><th className="num">Devices</th><th className="num">Sensitive (30d)</th><th className="num">Blocked (30d)</th><th className="num">Open alerts</th><SortTh filters={filters} col="last_seen_at" defaultDir="desc">Last seen</SortTh></tr></thead>
                <tbody>{d.items.map((u) => (
                  <tr key={u.id} {...clickableRow(() => nav(`/discover/users/${u.id}`))}>
                    <td><EntityLink to={`/discover/users/${u.id}`}>{u.display_name || u.email}</EntityLink><div className="sub">{u.email}{!u.enabled && ' · disabled'}</div></td>
                    <td className="sub">{u.department ?? '—'}</td><td className="sub">{u.source === 'entra' ? 'Entra ID' : u.source}</td>
                    <td className="num">{u.devices}</td><td className="num">{u.sensitive_30d}</td><td className="num">{u.blocked_30d}</td>
                    <td className={u.open_alerts ? 'num text-block' : 'num'}>{u.open_alerts || '—'}</td><td className="sub"><Ago at={u.last_seen_at} /></td>
                  </tr>
                ))}</tbody>
              </table></div>
              <Pager total={d.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
            </>
          )
        }
      </Q>
    </>
  );
}

export function UserDetail() {
  const { id } = useParams();
  const q = useApi<any>(`/users/${id}`);
  const ev = useEventDrawer();
  return (
    <Q q={q}>
      {({ user: u, groups, devices, apps, alerts, recentEvents }) => (
        <>
          <PageHead crumbs={<Link to="/discover/users">Users</Link>} title={u.display_name || u.email} sub={<>{u.email}{u.job_title && ` · ${u.job_title}`}{u.department && ` · ${u.department}`}</>}
            actions={<Link className="btn" to={`/investigate/activity?user=${u.id}`}>View activity</Link>} />
          <dl className="facts">
            <dt>Source</dt><dd>{u.source === 'entra' ? 'Microsoft Entra ID' : u.source}{!u.enabled && ' · disabled in directory'}</dd>
            <dt>Groups</dt><dd>{groups.length ? groups.map((g: any) => g.name).join(', ') : '—'}</dd>
            <dt>First seen</dt><dd>{fmtDate(u.first_seen_at)}</dd>
          </dl>
          <div className="cols cols-2">
            <Section title="Devices">
              {devices.length === 0 ? <Empty title="No enrolled browsers" /> : (
                <table className="t"><tbody>{devices.map((d: any) => <tr key={d.id}><td><Link to={`/discover/devices/${d.id}`}>{d.label}</Link><div className="sub">{browserLabel(d.browser)} {d.browser_version} · {d.managed ? 'managed' : 'unmanaged'}</div></td><td><Protection s={d.protection_state} /></td><td className="sub"><Ago at={d.last_heartbeat_at} /></td></tr>)}</tbody></table>
              )}
            </Section>
            <Section title="Alerts">
              {alerts.length === 0 ? <Empty title="No alerts" /> : (
                <table className="t"><tbody>{alerts.map((a: any) => <tr key={a.id}><td><Severity s={a.severity} /></td><td><Link to={`/investigate/alerts/${a.id}`}>{a.title}</Link></td><td><AlertStatus s={a.status} /></td></tr>)}</tbody></table>
              )}
            </Section>
          </div>
          <Section title="Applications used" aside="last 30 days">
            {apps.length === 0 ? <Empty title="No application usage reported" /> : (
              <table className="t"><thead><tr><th>Application</th><th>Category</th><th>Approval</th><th>Accounts</th><th className="num">Visits</th><th className="num">Sensitive</th></tr></thead>
                <tbody>{apps.map((a: any) => <tr key={a.id}><td><Link to={`/discover/apps/${a.id}`}>{a.name}</Link></td><td className="sub">{catLabel(a.category)}</td><td><Approval a={a.approval} /></td><td><div className="tags">{a.account_types.map((t: string) => <Account key={t} t={t} />)}</div></td><td className="num">{a.visits}</td><td className="num">{a.sensitive}</td></tr>)}</tbody></table>
            )}
          </Section>
          <Section title="Recent security events" aside={<Link to={`/investigate/activity?user=${u.id}`}>View all activity</Link>}><EventTable rows={recentEvents} onOpen={ev.open} compact /></Section>
          {ev.drawer}
        </>
      )}
    </Q>
  );
}

export function Groups() {
  const can = useCan();
  const q = useApi<any[]>('/groups');
  const users = useApi<any[]>('/users/options');
  const [name, setName] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [add, setAdd] = useState('');
  const create = useAction(() => api('/groups', { method: 'POST', body: { name } }), () => setName(''));
  const addMember = useAction((v: { g: string; u: string }) => api(`/groups/${v.g}/members`, { method: 'POST', body: { userIds: [v.u] } }), () => setAdd(''));
  const members = useApi<{ items: any[] }>(sel ? `/users?groupId=${sel}&limit=200` : null);
  const removeMember = useAction((v: { g: string; u: string }) => api(`/groups/${v.g}/members/${v.u}`, { method: 'DELETE' }));
  const selected = q.data?.find((g) => g.id === sel);
  return (
    <>
      <PageHead title="Groups" sub="Groups scope policies and exceptions. Entra ID groups are synchronised and read-only here; local groups can be edited." />
      {can('directory:write') && (
        <form className="filters" onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(undefined); }}>
          <input aria-label="New group name" placeholder="New local group name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          <button className="btn" disabled={!name.trim()}>Create group</button>
          <MutationError error={create.error} />
        </form>
      )}
      <div className="cols cols-2">
        <Q q={q}>
          {(rows) => rows.length === 0 ? <Empty title="No groups" /> : (
            <table className="t"><thead><tr><th>Group</th><th>Source</th><th className="num">Members</th><th className="num">Policies</th></tr></thead>
              <tbody>{rows.map((g) => (
                <tr key={g.id} {...clickableRow(() => setSel(g.id))} aria-selected={g.id === sel}>
                  <td>{g.name}<div className="sub">{g.description}</div></td><td className="sub">{g.source === 'entra' ? 'Entra ID' : 'Local'}</td><td className="num">{g.members}</td><td className="num">{g.policies}</td>
                </tr>
              ))}</tbody></table>
          )}
        </Q>
        <div>
          {!selected ? <Empty title="Select a group to see its members" /> : (
            <Section title={selected.name} aside={selected.source === 'entra' ? 'managed in Entra ID' : 'local group'}>
              {selected.source === 'manual' && can('directory:write') && (
                <form className="filters" onSubmit={(e) => { e.preventDefault(); if (add) addMember.mutate({ g: selected.id, u: add }); }}>
                  <select aria-label="Add member" value={add} onChange={(e) => setAdd(e.target.value)}><option value="">Add a member…</option>{users.data?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
                  <button className="btn" disabled={!add}>Add</button>
                </form>
              )}
              <Q q={members}>
                {(m) => m.items.length === 0 ? <Empty title="No members" /> : (
                  <table className="t"><tbody>{m.items.map((u) => <tr key={u.id}><td><Link to={`/discover/users/${u.id}`}>{u.display_name || u.email}</Link><div className="sub">{u.email}</div></td>
                    <td className="num">{selected.source === 'manual' && can('directory:write') && <button className="btn small danger" onClick={() => removeMember.mutate({ g: selected.id, u: u.id })}>Remove</button>}</td></tr>)}</tbody></table>
                )}
              </Q>
            </Section>
          )}
        </div>
      </div>
    </>
  );
}
